import React, { useState, useEffect, useCallback } from "react";
import { FaBuilding, FaTimes } from "react-icons/fa";
import api from "../../services/api.js";
import { useAuth } from "../../context/AuthContext.js";

// Someone invited this account into their company. The invite only arrives by
// email otherwise, so surface it in-app — accepting is the user's call.
const CompanyInvitationBanner = () => {
  const { isAuthenticated } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/v2/auth/company-invitations/mine");
      setInvitations(data?.data || []);
    } catch {
      setInvitations([]);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) load();
  }, [isAuthenticated, load]);

  const accept = async (invitation) => {
    setBusyId(invitation.id);
    setError("");
    try {
      await api.post("/api/v2/auth/company-invitations/accept", {
        invitationId: invitation.id,
      });
      // The auth cookie and every cached query are scoped to the old company.
      window.location.assign("/");
    } catch (err) {
      setError(
        err?.response?.data?.error || "Failed to accept this invitation",
      );
      setBusyId(null);
    }
  };

  const decline = async (invitation) => {
    setBusyId(invitation.id);
    setError("");
    try {
      await api.post("/api/v2/auth/company-invitations/decline", {
        invitationId: invitation.id,
      });
      setInvitations((list) => list.filter((i) => i.id !== invitation.id));
    } catch (err) {
      setError(
        err?.response?.data?.error || "Failed to decline this invitation",
      );
    } finally {
      setBusyId(null);
    }
  };

  const visible = invitations.filter((i) => !dismissed.includes(i.id));

  if (!isAuthenticated || visible.length === 0) return null;

  return (
    <div className="border-b border-brand-yellow/40 bg-brand-gold-soft">
      {visible.map((invitation) => (
        <div
          key={invitation.id}
          className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex items-start gap-3">
            <FaBuilding className="mt-1 h-4 w-4 shrink-0 text-brand-gold" />
            <div className="text-sm text-brand-gold">
              <span className="font-semibold">
                {invitation.invitedByName || "An admin"} invited you to join{" "}
                {invitation.companyName}.
              </span>{" "}
              Accepting moves your account there — you will leave your current
              company and its groups.
              {error && <span className="block text-red-700">{error}</span>}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              onClick={() => accept(invitation)}
              disabled={busyId === invitation.id}
              className="rounded-lg bg-brand-yellow px-3.5 py-1.5 text-sm font-semibold text-brand-ink transition-colors hover:bg-brand-yellow-dark disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busyId === invitation.id ? "Working…" : "Join"}
            </button>
            <button
              onClick={() => decline(invitation)}
              disabled={busyId === invitation.id}
              className="rounded-lg border border-brand-yellow/40 bg-white px-3.5 py-1.5 text-sm font-medium text-brand-gold transition-colors hover:bg-brand-gold-soft disabled:cursor-not-allowed disabled:opacity-50"
            >
              Decline
            </button>
            <button
              onClick={() => setDismissed((list) => [...list, invitation.id])}
              aria-label="Hide this invitation for now"
              title="Hide for now"
              className="rounded-lg p-1.5 text-brand-gold transition-colors hover:bg-brand-gold-soft hover:text-brand-gold"
            >
              <FaTimes className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CompanyInvitationBanner;
